/**
 * Obsidian vault import transformations
 *
 * Handles Obsidian-specific processing:
 * - Reading .obsidian/app.json for attachment folder configuration
 * - Adding note titles (as H1 or frontmatter)
 * - Converting ![[...]] embeds to standard Markdown links
 */

import matter from 'gray-matter'

/**
 * Obsidian vault settings from .obsidian/app.json
 */
export type ObsidianSettings = {
  /**
   * Attachment folder configuration:
   * - './' = same folder as note
   * - '/' = vault root
   * - './subfolder' = subfolder relative to note's folder
   * - 'Subfolder' = absolute path from vault root
   */
  attachmentFolderPath?: string
}

/**
 * Strip first directory from webkit relative path to get vault-relative path
 */
function stripFirstDirectory(path: string): string {
  const firstSlash = path.indexOf('/')
  return firstSlash === -1 ? path : path.slice(firstSlash + 1)
}

/**
 * Parse Obsidian settings from .obsidian/app.json if present
 */
export async function parseObsidianSettings(
  files: FileList
): Promise<ObsidianSettings | undefined> {
  for (const file of files) {
    const raw = file.webkitRelativePath || file.name
    const path = stripFirstDirectory(raw)

    if (path === '.obsidian/app.json') {
      try {
        const json: unknown = JSON.parse(await file.text())
        if (json && typeof json === 'object' && 'attachmentFolderPath' in json && typeof json.attachmentFolderPath === 'string') {
          return { attachmentFolderPath: json.attachmentFolderPath }
        }
      } catch {
        // Invalid JSON, fall back to default behavior
        return undefined
      }
      break
    }
  }

  return undefined
}

/**
 * Ensure note has a title (as frontmatter title or H1 heading)
 *
 * Uses gray-matter for robust YAML frontmatter parsing to avoid
 * misidentifying horizontal rules (---) as frontmatter delimiters.
 *
 * @param content - Note content
 * @param expectedTitle - Title to add (typically filename without extension)
 * @returns Content with title added if needed
 */
export function ensureTitle(content: string, expectedTitle: string): string {
  try {
    const parsed = matter(content)

    // If frontmatter exists and has title, return as-is
    if (parsed.data.title || parsed.data.name) {
      return content
    }

    // If frontmatter exists but no title, add it
    if (Object.keys(parsed.data).length > 0) {
      parsed.data.title = expectedTitle
      return matter.stringify(parsed.content, parsed.data)
    }

    // No frontmatter: check for H1
    const trimmed = parsed.content.replace(/^\uFEFF?/, '')
    if (!/^#\s+/.test(trimmed)) {
      return `# ${expectedTitle}\n${content}`
    }

    return content
  } catch {
    // Fallback: return content unchanged if parsing fails
    return content
  }
}

/**
 * Resolve base attachment folder path according to Obsidian settings
 *
 * @param noteFolder - Folder containing the note (e.g., 'Projects/Work')
 * @param attachmentPath - Value from .obsidian/app.json
 * @returns Base path for resolving attachment links
 */
function resolveAttachmentBase(
  noteFolder: string,
  attachmentPath?: string
): string {
  if (!attachmentPath || attachmentPath === './') {
    return noteFolder
  }

  if (attachmentPath === '/') {
    return '' // Vault root
  }

  if (attachmentPath.startsWith('./')) {
    const relative = attachmentPath.slice(2)
    return [noteFolder, relative].filter(Boolean).join('/')
  }

  return attachmentPath // Absolute from vault root
}

/**
 * Build candidate paths for an attachment target
 *
 * @param target - Target from ![[target]] syntax
 * @param base - Base folder for resolving relative paths
 * @returns Array of possible paths to try
 */
function buildAttachmentCandidates(target: string, base: string): string[] {
  const joined = [base, target].filter(Boolean).join('/')
  const candidates: string[] = [joined]

  // If no extension, try common image/file extensions
  if (!/\.[\da-z]+$/i.test(target)) {
    const extensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'tiff', 'bmp', 'pdf']
    for (const extension of extensions) {
      candidates.push(`${joined}.${extension}`)
    }
  }

  return candidates
}

/**
 * Build basename index for fallback attachment resolution
 */
function buildBasenameIndex(
  attachmentPaths: Set<string>
): Map<string, string[]> {
  const index = new Map<string, string[]>()

  for (const path of attachmentPaths) {
    const basename = path.split('/').pop() ?? path
    const existing = index.get(basename) ?? []
    existing.push(path)
    index.set(basename, existing)
  }

  return index
}

/**
 * Compute relative path from note's folder to attachment
 * Converts vault-relative attachment path to note-relative path
 *
 * @param fromFolder - Note's folder (vault-relative, e.g., 'Lists' or 'Projects/Work')
 * @param toPath - Attachment path (vault-relative, e.g., 'test-image.jpg' or 'Assets/image.png')
 * @returns Relative path from note folder to attachment (e.g., '../test-image.jpg')
 */
function relativePath(fromFolder: string, toPath: string): string {
  if (!fromFolder) return toPath

  const fromSegments = fromFolder.split('/').filter(Boolean)
  const toSegments = toPath.split('/').filter(Boolean)

  // Find common prefix
  let commonIndex = 0
  while (
    commonIndex < fromSegments.length &&
    commonIndex < toSegments.length &&
    fromSegments[commonIndex] === toSegments[commonIndex]
  ) {
    commonIndex++
  }

  // Build relative path with ../ for going up
  const upLevels = fromSegments.length - commonIndex
  const parts: string[] = []
  for (let index = 0; index < upLevels; index++) {
    parts.push('..')
  }
  parts.push(...toSegments.slice(commonIndex))

  return parts.join('/')
}

/**
 * Apply Obsidian-specific transformations to note content
 *
 * @param content - Original note content
 * @param ctx - Transformation context
 * @returns Transformed content
 */
export function applyObsidianTransforms(
  content: string,
  context: {
    path: string
    isCalendar: boolean
    settings?: ObsidianSettings
    attachmentIndex: Set<string>
  }
): string {
  let result = content

  // Add title for project notes (skip calendar notes)
  if (!context.isCalendar) {
    const expectedTitle = (context.path.split('/').pop() ?? 'Untitled').replace(
      /\.[^./]+$/,
      ''
    )
    result = ensureTitle(result, expectedTitle)
  }

  // Convert ![[...]] embeds to standard Markdown links
  const folder = context.path.includes('/')
    ? context.path.slice(0, context.path.lastIndexOf('/'))
    : ''

  const base = resolveAttachmentBase(
    folder,
    context.settings?.attachmentFolderPath
  )

  const basenameIndex = buildBasenameIndex(context.attachmentIndex)

  // Pattern matches ![[filename]] with optional |width parameter
  // Ignores heading/block references after # or ^
  result = result.replaceAll(
    /!\[\[([^#\]^|]+)(?:\|\d+)?]]/g,
    (_match, rawTarget: string) => {
      const target = String(rawTarget).trim()

      // Try direct path resolution first
      const candidates = buildAttachmentCandidates(target, base)
      let resolved = candidates.find(p => context.attachmentIndex.has(p))

      // Fallback: search by basename across entire vault
      if (!resolved) {
        const basename = target.includes('/') ? target.split('/').pop() ?? target : target

        // Try exact basename match and with extensions
        const searchNames = [
          basename,
          ...['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'tiff', 'bmp', 'pdf'].map(
            extension => `${basename}.${extension}`
          )
        ]

        for (const name of searchNames) {
          const matches = basenameIndex.get(name)
          if (matches && matches.length > 0) {
            // Prefer attachment under base folder if any
            const preferred =
              matches.find(m => base && m.startsWith(base + '/')) ?? matches[0]
            resolved = preferred
            break
          }
        }
      }

      // If still not found, preserve as link so pipeline can warn
      if (!resolved) {
        const basename = target.split('/').pop() ?? target
        return `![${basename}](${target})`
      }

      // Convert vault-relative path to note-relative path
      // This ensures parseMarkdownLinks resolves it back to the same vault-relative path
      const basename = resolved.split('/').pop() ?? resolved
      const linkPath = relativePath(folder, resolved)
      return `![${basename}](${linkPath})`
    }
  )

  return result
}
